import React, { useEffect, useRef } from 'react'
import styles from './ActionTabs.module.scss'
import { Building, Package as PackageIcon, Users, Store, Shield } from 'lucide-react'
import { registerTutorialRef } from '../../../utils/tutorialRefsRegistry.ts'
import { fireAnalytics } from '../../../utils/firebase'
import {
  getSessionId,
  getDeviceCategory,
  getTimeOnTab,
  setTabStartTime,
  trackTabViewed,
} from '../../../utils/analyticsHelpers.ts'

export type ActionTab = 'obras' | 'materiales' | 'personas' | 'marketplace' | 'documentacion' | 'admin'

interface ActionTabsProps {
  selected: ActionTab
  onChange: (tab: ActionTab) => void
  showAdminTab?: boolean
  userData?: any
}

const ActionTabs: React.FC<ActionTabsProps> = ({ selected, onChange, showAdminTab = false, userData }) => {
  const tabsContainerRef = useRef<HTMLDivElement>(null)
  const tabsScrollRef = useRef<HTMLDivElement>(null)
  const activeTabRef = useRef<HTMLButtonElement>(null)
  const previousTabRef = useRef<ActionTab | null>(null)

  const baseTabs = [
    { id: 'obras' as ActionTab, label: 'Obras', icon: <Building size={18} /> },
    { id: 'materiales' as ActionTab, label: 'Materiales', icon: <PackageIcon size={18} /> },
    { id: 'personas' as ActionTab, label: 'Personas', icon: <Users size={18} /> },
    { id: 'marketplace' as ActionTab, label: 'Marketplace', icon: <Store size={18} /> },
    /* { id: 'documentacion' as ActionTab, label: 'Documentación', icon: <FileText size={18} /> } */
  ]

  const adminTab = { id: 'admin' as ActionTab, label: 'Admin', icon: <Shield size={18} /> }

  const tabs = showAdminTab ? [...baseTabs, adminTab] : baseTabs

  // Función para centrar el tab activo
  const centerActiveTab = () => {
    if (activeTabRef.current && tabsScrollRef.current) {
      const isMobile = window.innerWidth <= 600
      const isTablet = window.innerWidth <= 1024 && window.innerWidth > 600
      
      if (isMobile || isTablet) {
        const tabsContainer = tabsScrollRef.current
        const activeTab = activeTabRef.current
        
        const containerWidth = tabsContainer.clientWidth
        const tabLeft = activeTab.offsetLeft
        const tabWidth = activeTab.clientWidth
        
        // Calcular la posición para centrar el tab
        const scrollLeft = tabLeft - (containerWidth / 2) + (tabWidth / 2)
        
        tabsContainer.scrollTo({
          left: Math.max(0, scrollLeft),
          behavior: 'smooth'
        })
      }
    }
  }

  useEffect(() => {
    if (previousTabRef.current !== null && previousTabRef.current !== selected) {
      const timeOnPreviousTab = getTimeOnTab(previousTabRef.current)
      
      fireAnalytics('dashboard_tab_change', {
        user_id: userData?.id || '',
        session_id: getSessionId(),
        from_tab: previousTabRef.current,
        to_tab: selected,
        time_on_previous_tab: timeOnPreviousTab,
        device_category: getDeviceCategory(),
        category: 'dashboard',
        action: 'tab_change'
      })
      
      trackTabViewed(selected)
    } else if (previousTabRef.current === null) {
      trackTabViewed(selected)
    }
    
    setTabStartTime(selected)
    previousTabRef.current = selected
    centerActiveTab()
  }, [selected, userData])

  // Centrar el tab activo cuando cambia el tamaño de ventana
  useEffect(() => {
    const handleResize = () => {
      centerActiveTab()
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [selected])

  useEffect(() => {
    if (tabsScrollRef.current) {
      registerTutorialRef('action-tabs', tabsScrollRef.current)
    }
    return () => {
      registerTutorialRef('action-tabs', null)
    }
  }, [])

  return (
    <div ref={tabsContainerRef} className={styles.tabsContainer} data-tutorial="action-tabs">
      <div ref={tabsScrollRef} className={styles.tabs}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            ref={selected === tab.id ? activeTabRef : null}
            className={`${styles.tab} ${selected === tab.id ? styles.active : ''}`}
            onClick={() => onChange(tab.id)}
          >
            <span className={styles.tabIcon}>{tab.icon}</span>
            <span className={styles.tabText}>{tab.label}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

export default ActionTabs

